import React, { useEffect, useState } from "react";
import { useGetChatLogs } from "../../api/useGetChatLogs";
import { Button, DatePicker, Input, Modal, Select, Table } from "antd";
import {
  InfoCircleFilled,
  LaptopOutlined,
  MessageOutlined,
} from "@ant-design/icons";
import "./ChatLogs.scss";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import "dayjs/locale/en-gb";
import "dayjs/plugin/updateLocale";
export const ChatLogs = () => {
  const navigate = useNavigate();
  const urlParams = new URLSearchParams(window.location.search);
  const [modal, contextHolder] = Modal.useModal();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [total, setTotal] = useState(15);
  const [orderCriteria, setOrderCriteria] = useState(
    urlParams.get("order") ?? "last_message"
  );
  const [direction, setDirection] = useState(
    urlParams.get("direction") ?? "asc"
  );

  const [category, setCategory] = useState(urlParams.get("category") ?? "user");
  const [dateFrom, setDateFrom] = useState(
    urlParams.get("from") !== "undefined"
      ? decodeURIComponent(urlParams.get("from"))
      : undefined
  );
  const [dateTo, setDateTo] = useState(
    urlParams.get("to") !== "undefined"
      ? decodeURIComponent(urlParams.get("to"))
      : undefined
  );
  const [searchString, setSearchString] = useState(urlParams.get("search"));
  const [enabled, setEnabled] = useState(true);

  const lastMidnight = dayjs().startOf("day");
  const rangePresets = [
    {
      label: "Today",
      value: [lastMidnight, dayjs()],
    },
    {
      label: "Yesterday",
      value: [lastMidnight.add(-1, "d"), lastMidnight],
    },
    {
      label: "Last 7 Days",
      value: [lastMidnight.add(-7, "d"), lastMidnight],
    },
    {
      label: "Previous week",
      value: [dayjs().startOf("week").add(-7, "d"), dayjs().startOf("week")],
    },
    {
      label: "Last 14 Days",
      value: [lastMidnight.add(-14, "d"), lastMidnight],
    },
    {
      label: "Last 30 Days",
      value: [lastMidnight.add(-30, "d"), lastMidnight],
    },
    {
      label: "Previous Month",
      value: [
        dayjs().startOf("month").add(-1, "month"),
        dayjs().startOf("month"),
      ],
    },
    {
      label: "Last 90 Days",
      value: [lastMidnight.add(-90, "d"), lastMidnight],
    },
  ];
  const { data, isLoading, isSuccess } = useGetChatLogs(
    pageSize,
    page,
    orderCriteria,
    direction,
    category,
    searchString,
    dateFrom,
    dateTo,
    window.location.search,
    enabled
  );
  const { RangePicker } = DatePicker;
  useEffect(() => {
    data &&
      setTotal((prevState) =>
        prevState !== data?.meta?.total ? data?.meta?.total : prevState
      );
  }, [data]);
  useEffect(() => {
    setEnabled(false);
  }, [isSuccess]);
  const { Search } = Input;
  const handleSearch = (text) => {
    urlParams.set("search", text ?? "");
    urlParams.set("category", category ?? "");
    urlParams.set("direction", direction ?? "");
    urlParams.set("order", orderCriteria ?? "");
    urlParams.set("from", encodeURIComponent(dateFrom) ?? "%02%03");
    urlParams.set("to", encodeURIComponent(dateTo) ?? "%02%03");
    window.location.search = urlParams;
    setSearchString(text);
    setEnabled(true);
  };
  const disabledDate = (current) => {
    return current && current > dayjs().endOf("day");
  };
  const onRangeChange = (dates, dateStrings) => {
    if (dates) {
      setDateFrom(dateStrings[0]);
      setDateTo(dateStrings[1]);
    } else {
      setDateFrom(undefined);
      setDateTo(undefined);
    }
  };
  const handleCategory = (text) => {
    setCategory(text);
  };
  const handleDirection = (text) => {
    setDirection(text);
  };
  const handleOrder = (text) => {
    setOrderCriteria(text);
  };
  const orderCriteriaOptions = [
    {
      label: "Last message",
      value: "last_message",
    },
    {
      label: "Total message",
      value: "total_message",
    },
  ];
  const orderDirection = [
    {
      label: "Ascending",
      value: "asc",
    },
    {
      label: "Descending",
      value: "desc",
    },
  ];
  const searchCriteria = [
    {
      label: "Agent",
      value: "agent",
    },
    {
      label: "User",
      value: "user",
    },
    {
      label: "Fictitious user",
      value: "fictitious_user",
    },
  ];
  const confirm = (content, date) => {
    modal.confirm({
      title: `Latest agent message sent on ${new Date(
        date
      ).toLocaleDateString()}:`,
      icon: <InfoCircleFilled />,
      content,
      destroyOnClose: true,
      cancelText: "OK",
      okButtonProps: { style: { display: "none" } },
    });
  };
  const columns = [
    {
      title: "Agent name",
      dataIndex: "agent_name",
      key: "agent_name",
      width: "14%",
    },
    {
      title: "Fictitious profile",
      dataIndex: "fictitious_profile",
      key: "fictitious_profile",
    },
    {
      title: "Real user",
      dataIndex: "real_user",
      key: "real_user",
    },
    {
      title: "Start of conversation",
      dataIndex: "conversation_started_at",
      key: "conversation_started_at",
      render: (val) => <div>{moment(val).format("DD.MM.yyyy HH:mm")}</div>,
    },
    {
      title: "Time of last exchange",
      dataIndex: "last_exchange_at",
      key: "last_exchange_at",
      render: (val) => <div>{moment(val).format("DD.MM.yyyy HH:mm")}</div>,
    },
    {
      title: "Total exchange",
      dataIndex: "total_messages_exchanged",
      key: "total_messages_exchanged",
      width: "14%",
    },
    {
      title: "Actions",
      key: "actions",
      render: (row) => (
        <div>
          <div
            className={"table-button"}
            onClick={() => navigate(`/agentView/${row.id}`)}
          >
            SEE MORE <LaptopOutlined />
          </div>
          <div
            className={"table-button"}
            onClick={() =>
              confirm(row.last_agent_message?.body, row.last_exchange_at)
            }
          >
            LAST AGENT MESSAGE <MessageOutlined />
          </div>
        </div>
      ),
    },
  ];
  console.log({ dateTo, dateFrom });
  return (
    <>
      <div className={"filter"}>
        <div className={"search-group"}>
          <div className={"group-item label"}>Date filter:</div>
          <RangePicker
            presets={rangePresets}
            onChange={onRangeChange}
            format="YYYY-MM-DD HH:mm:ss"
            disabledDate={disabledDate}
            locale={"en"}
            defaultValue={[
              dateFrom !== "null" && dateFrom !== undefined
                ? dayjs(dateFrom)
                : null,
              dateTo !== "null" && dateTo !== undefined ? dayjs(dateTo) : null,
            ]}
            allowEmpty={[true, true]}
          />
        </div>
        <div className={"search-group"}>
          <div className={"group-item label"}>Order by:</div>
          <Select
            options={orderCriteriaOptions}
            className={"group-item"}
            value={orderCriteria}
            onChange={handleOrder}
          />
          <div className={"group-item label"}>Direction:</div>
          <Select
            options={orderDirection}
            className={"group-item"}
            value={direction}
            onChange={handleDirection}
          />
        </div>
        <div className={"search-group search"}>
          <div className={"group-item label"}>Search by:</div>
          <Select
            options={searchCriteria}
            className={"group-item"}
            value={category}
            onChange={handleCategory}
          />
          <Search
            className={"group-item  search-input"}
            defaultValue={searchString}
            onSearch={(e) => handleSearch(e)}
            enterButton={<Button type={"primary"}>Go</Button>}
          />
        </div>
      </div>
      {contextHolder}
      <div className={"table-container"}>
        <Table
          dataSource={data?.data}
          columns={columns}
          rowKey={(record, index) => index}
          scroll={{ y: 500, x: true }}
          tableLayout={"fixed"}
          loading={isLoading}
          pagination={{
            position: ["topRight"],
            pageSize,
            onChange: (page) => {
              setPage(page);
              setEnabled(true);
            },
            total,
            onShowSizeChange: (e, size) => setPageSize(size),
          }}
        />
      </div>
    </>
  );
};
